import cx from "classnames";
import { useEffect, useState } from "react";

import { appstr } from "@coreapp/utils/appstr";
import {
  BuildSystemFieldOptions,
  BuildTextFieldsOptions,
  enumSystemFieldsMode
} from "@/Utils/DataLib";
import { FieldSelectListProps } from "@/Utils/ComponentLib";
import { FieldText, Select } from "@theorgchartdev/storybook";
import { getComparisonLabel } from "./RulePairInput.util";
import { GetDoc } from "@/Utils/StateLib";
import { getValueMode, RulePairValueInput, ValueModeEnum } from "./RulePairValueInput";
import { GetFieldLabel } from "@coreapp/utils/GetFieldLabel";
import { enumFieldType, enumObjClassNames } from "@coreapp/constants";

export const RULE_PROPS = {
  comparisons: [
    "equals",
    "notequal",
    "startswith",
    "contains",
    "doesnotcontain",
    "isempty",
    "isnotempty",
    "greater",
    "less",
    "greaterequal",
    "lessequal",
    "inlist",
    "notinlist"
  ],
  fieldComparisons: ["equals", "notequal", "greater", "less", "greaterequal", "lessequal"],
  matchsubs: ["matchonly", "directonly", "allsubs"]
};

RULE_PROPS.numberComparisons = ["range", ...RULE_PROPS.comparisons];

RULE_PROPS.dateComparisons = ["daterange", ...RULE_PROPS.comparisons];

const RANGES = ["range", "daterange"];

export const commonMenuProps = {
  bold: true,
  optionSize: "small",
  popoverProps: {
    flipOptions: {
      mainAxis: false
    }
  }
};

/**
 *
 * @typedef {object} RulePairInputProps
 * @property {QRulePair} value
 * @property {function} onChange
 * @property {boolean} [allowHierarchyFields]
 * @property {string} [variant]
 * @property {boolean} [editing]
 * @property {boolean} [hideComparisonInput]
 * @property {boolean} [showFieldComparisons]
 * @property {boolean} [blocksStructure]
 * @property {string} [direction]
 * @property {string} [dataTestIdPrefix]
 * @property {string} [popoverTestIdPrefix]
 * @property {boolean} [requiredMarker]
 * @property {object} [chartData]
 * @property {"small" | "medium"} [selectSize]
 * @property {{ hasError: boolean }} [fieldError]
 * @property {{ hasError: boolean }} [valueError]
 *
 * @param {RulePairInputProps} props
 * @returns
 */
export const RulePairInput = props => {
  const dataTestIdPrefix = props.dataTestIdPrefix ?? "rule-input-menu";
  const popoverTestIdPrefix = props.popoverTestIdPrefix ?? "rule-input-menu";
  const afterLabelElem = props.requiredMarker ? (
    <span className={"ocn-form-label-required"} style={{ marginLeft: "4px" }}>
      *
    </span>
  ) : null;
  const customFields = props?.chartData?.customFields;

  // Check for legacy comma delimited strings
  useEffect(() => {
    const initValue = props.value.GetValue();
    if (getValueMode(props.value) !== ValueModeEnum.MultiSelect || Array.isArray(initValue)) {
      return;
    }

    const newPair = props.value.Clone();
    const newValues = initValue.split(",");
    newPair.UpdateValue(newValues);
    props.onChange(newPair);
  }, []);

  const clearValue = (rulePair, isFieldToField) => {
    if (getValueMode(rulePair) === ValueModeEnum.MultiSelect) {
      rulePair.UpdateValue([]);
      return;
    }

    if (getValueMode(rulePair) === ValueModeEnum.Range) {
      rulePair.UpdateValue([null, null]);
      return;
    }

    if (getValueMode(rulePair) === ValueModeEnum.DateRange) {
      rulePair.UpdateValue({
        from: {
          dateType: "today",
          value: null
        },
        to: {
          dateType: null,
          value: null
        }
      });
      return;
    }

    if (isFieldToField) {
      rulePair.UpdateValue({});
      return;
    }

    rulePair.UpdateValue("");
  };

  const handleUpdate = property => target => {
    const newPair = props.value.Clone();
    switch (property) {
      case "value":
        newPair.UpdateValue(target.value);
        break;
      case "field-value": {
        const { fieldDef } = target.eventInfo;
        const fieldName = fieldDef.GetFieldName();
        const fieldId = fieldDef.GetFieldID();
        const fieldClassName = fieldDef.GetFieldClassName();
        newPair.UpdateValue({ fieldName, fieldId, fieldClassName });
        break;
      }
      case "comparison":
        const { isFieldToField, comparison } = target.eventInfo;
        newPair.UpdateComparison(comparison);

        const oldIsFieldToField = props.value.IsFieldToFieldComparison();
        const wasRange = RANGES.includes(props.value.GetComparison());

        if (oldIsFieldToField !== isFieldToField || wasRange === !RANGES.includes(comparison)) {
          clearValue(newPair, isFieldToField);
        }

        const oldValueMode = getValueMode(props.value);
        const newValueMode = getValueMode(newPair);
        if (oldValueMode !== newValueMode) {
          // Clear value when changing to or from a list
          if (
            oldValueMode === ValueModeEnum.MultiSelect ||
            newValueMode === ValueModeEnum.MultiSelect
          ) {
            clearValue(newPair, isFieldToField);
          }
        }
        break;
      case "field":
        const { fieldDef } = target.eventInfo;
        const fieldName = fieldDef.GetFieldName();
        const fieldId = fieldDef.GetFieldID();
        const fieldClassName = fieldDef.GetFieldClassName();
        newPair.UpdateFieldName(fieldName, fieldId, fieldClassName);
        clearValue(newPair, newPair.IsFieldToFieldComparison());

        if (
          props.value.GetComparison() === "range" &&
          fieldDef.GetFieldType() !== enumFieldType.number
        ) {
          newPair.UpdateComparison("equals");
          clearValue(newPair, false);
        }

        if (
          props.value.GetComparison() === "daterange" &&
          fieldDef.GetFieldType() !== enumFieldType.date
        ) {
          newPair.UpdateComparison("equals");
          clearValue(newPair, false);
        }
        break;
    }

    props.onChange(newPair);
  };

  const handleUpdateTextFields = property => target => {
    const newPair = props.value.Clone();
    const fieldName = target.label;
    const fieldId = target.label;
    const fieldClassName = target.value?.split("-")[0] || enumObjClassNames.box;

    switch (property) {
      case "field-value":
        newPair.UpdateValue({ fieldName, fieldId, fieldClassName });
        break;
      case "field":
        newPair.UpdateFieldName(fieldName, fieldId, fieldClassName);
        clearValue(newPair, newPair.IsFieldToFieldComparison());
        // All fields are text fields, so no way to have ranges.
        break;
      default:
        break;
    }

    props.onChange(newPair);
  };

  const doc = GetDoc();
  const chartID = doc?.GetChartID();

  const createFieldOptions = (handleSelect, dataTestIdPredicate) => {
    let blockList = [];
    if (!props.allowHierarchyFields) {
      blockList = ["SubChart Level", "ReportsToName", "Also Reports To"];
    }

    if (customFields) {
      return BuildTextFieldsOptions(
        customFields,
        handleSelect,
        {},
        enumSystemFieldsMode.filterable,
        `${dataTestIdPrefix}-${dataTestIdPredicate}-`,
        blockList
      );
    }
    return BuildSystemFieldOptions(
      handleSelect,
      {},
      enumSystemFieldsMode.filterable,
      `${dataTestIdPrefix}-${dataTestIdPredicate}-`,
      blockList
    );
  };

  const fieldOptions = createFieldOptions(
    (customFields ? handleUpdateTextFields : handleUpdate)("field"),
    "field"
  );
  const fieldValueOptions = createFieldOptions(
    (customFields ? handleUpdateTextFields : handleUpdate)("field-value"),
    "field-value"
  );

  const field = props.value.GetFieldName();
  const fieldClassName = props.value.GetFieldClassName();
  const fieldDef = props.value.GetReferenceFieldDef(doc);
  const comparison = props.value.GetComparison();
  const value = props.value.GetValue();
  const isFieldToField = props.value.IsFieldToFieldComparison();
  const valueString = props.value.GetValueDisplayString(doc);
  const fieldLabel = customFields ? field : GetFieldLabel(fieldDef, true);

  const isCompact = props.variant === "compact";
  const selectSize = props.selectSize ?? "small";
  const menuProps = { ...commonMenuProps, optionSize: selectSize };
  const fieldError = props.fieldError?.errorString;
  const fieldHasError = !!props.fieldError?.hasError;

  if (!props.editing) {
    return props.blocksStructure ? (
      <div
        className={cx("oc-rule-pair-input-wrapper", {
          "oc-rule-pair-input-wrapper--row": props.direction === "column",
          "oc-rule-pair-input-wrapper--compact": isCompact
        })}
      >
        <div className="oc-rule-pair-titles">
          <div className="oc-rule-pair-title">{appstr("Field")}</div>
          <div className="oc-rule-pair-title">{appstr("Comparison")}</div>
          <div className="oc-rule-pair-title">{appstr("Value")}</div>
        </div>
        <div className="oc-rule-pair-values">
          <div className="oc-rule-pair-value">{fieldLabel}</div>
          <div className="oc-rule-pair-value">
            {getComparisonLabel(comparison, false, fieldDef)}
          </div>
          <div className="oc-rule-pair-value">{valueString}</div>
        </div>
      </div>
    ) : (
      <div
        className={cx("oc-rule-pair-input-wrapper", {
          "oc-rule-pair-input-wrapper--row": props.direction === "column",
          "oc-rule-pair-input-wrapper--compact": isCompact
        })}
      >
        <FieldText field={appstr("Field")} value={fieldLabel} valueSize={isCompact && "small"} />
        <FieldText
          field={appstr("Comparison")}
          value={getComparisonLabel(comparison, isFieldToField, fieldDef)}
          valueSize={isCompact && "small"}
        />
        <FieldText field={appstr("Value")} value={valueString} valueSize={isCompact && "small"} />
      </div>
    );
  }

  const getComparisonOptions = () => {
    const fieldType = fieldDef?.GetFieldType();
    let ruleSet;

    switch (fieldType) {
      case enumFieldType.number:
        ruleSet = RULE_PROPS.numberComparisons;
        break;
      case enumFieldType.date:
        ruleSet = RULE_PROPS.dateComparisons;
        break;
      default:
        ruleSet = RULE_PROPS.comparisons;
    }

    const standardComparisons = ruleSet.map(item => ({
      dataTestId: `${dataTestIdPrefix}-comp--${item}`,
      label: getComparisonLabel(item, false, fieldDef),
      value: item,
      eventInfo: {
        comparison: item,
        isFieldToField: false
      },
      clickHandler: handleUpdate("comparison")
    }));

    if ([enumFieldType.number, enumFieldType.date].includes(fieldType)) {
      standardComparisons[0].divider = true;
    }

    if (!props.showFieldComparisons) {
      return standardComparisons;
    }

    standardComparisons[standardComparisons.length - 1].divider = true;

    const fieldComparisons = RULE_PROPS.fieldComparisons.map(item => ({
      dataTestId: `${dataTestIdPrefix}-comp--field-${item}`,
      label: getComparisonLabel(item, true, fieldDef),
      value: `field-${item}`,
      eventInfo: {
        comparison: item,
        isFieldToField: true
      },
      clickHandler: handleUpdate("comparison")
    }));

    return standardComparisons.concat(fieldComparisons);
  };

  return (
    <div
      className={cx("oc-rule-pair-input-wrapper", {
        "oc-rule-pair-input-wrapper--row": props.direction === "column"
      })}
    >
      <Select
        inputOnly={false}
        label={appstr("Field")}
        require={!!props.requiredMarker}
        message={fieldError}
        noMargin
        listProps={FieldSelectListProps({
          enableSearch: fieldOptions.length !== 2,
          inputProps: { dataTestId: `${dataTestIdPrefix}-field--search` }
        })}
        value={`${fieldClassName}-${field}`}
        options={fieldOptions}
        placeholder={appstr("Select field")}
        fillWidth
        listWidth={"fill"}
        status={fieldHasError ? "error" : "default"}
        aria-invalid={fieldHasError || undefined}
        {...menuProps}
        selectSize={selectSize}
        dataTestId={`${dataTestIdPrefix}-field`}
        popoverProps={{
          ...menuProps.popoverProps,
          floatingProps: {
            ...menuProps.popoverProps?.floatingProps,
            "data-testid": `${popoverTestIdPrefix}-field-popover`
          }
        }}
      />
      {!props.hideComparisonInput ? (
        <Select
          inputOnly={false}
          label={appstr("Comparison")}
          require={!!props.requiredMarker}
          noMargin
          value={isFieldToField ? `field-${comparison}` : comparison}
          options={getComparisonOptions()}
          fillWidth
          listWidth={"fill"}
          {...menuProps}
          selectSize={selectSize}
          dataTestId={`${dataTestIdPrefix}-comp`}
          popoverProps={{
            ...menuProps.popoverProps,
            floatingProps: {
              ...menuProps.popoverProps?.floatingProps,
              "data-testid": `${popoverTestIdPrefix}-comp-popover`
            }
          }}
        />
      ) : null}
      {isFieldToField ? (
        <Select
          inputOnly={false}
          label={appstr("Field")}
          noMargin
          listProps={FieldSelectListProps({
            enableSearch: fieldOptions.length !== 2,
            inputProps: { dataTestId: `${dataTestIdPrefix}-field-value--search` }
          })}
          value={`${value.fieldClassName}-${value.fieldName}`}
          options={fieldValueOptions}
          fillWidth
          listWidth={"fill"}
          {...menuProps}
          selectSize={selectSize}
          dataTestId={`${dataTestIdPrefix}-field-value`}
          popoverProps={{
            ...menuProps.popoverProps,
            floatingProps: {
              ...menuProps.popoverProps?.floatingProps,
              "data-testid": `${popoverTestIdPrefix}-field-value-popover`
            }
          }}
        />
      ) : (
        <RulePairValueInput
          dataTestIdPrefix={dataTestIdPrefix}
          popoverTestIdPrefix={popoverTestIdPrefix}
          onUpdate={handleUpdate}
          rulePair={props.value}
          menuProps={menuProps}
          selectSize={selectSize}
          afterLabelElem={afterLabelElem}
          chartData={props.chartData}
          valueError={props.valueError}
        />
      )}
    </div>
  );
};
